<script setup lang="ts">
import WrIcon from "../global/WrIcon.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

/* Fixed internal links to the other services/mobility pages (crawl equity; varied anchor text) */
const related = [
  {
    title: "Explore all mobility services",
    to: "/services/mobility",
    svgIcon: "streamline:compass-navigator",
  },
  {
    title: "React Native app development",
    to: "/services/mobility/reactnative-app-development",
    /* Inline SVG atom (React logo) — no streamline atom/react icon available */
    reactAtom: true,
  },
  {
    title: "Ionic app development",
    to: "/services/mobility/ionic-app-development",
    svgIcon: "streamline:browser-website-1",
  },
  {
    title: "Tizen app development",
    to: "/services/mobility/tizen-app-development",
    svgIcon: "streamline:phone-mobile-phone",
  },
  {
    title: "NativeScript app development",
    to: "/services/mobility/nativescript-app-development",
    svgIcon: "streamline:code-monitor-1",
  },
  {
    title: "iBeacon app development",
    to: "/services/mobility/ibeacon-app-development",
    svgIcon: "streamline:wave-signal",
  },
];
</script>

<template>
  <section class="relative">
    <div class="text-center">
      <span class="fl-eyebrow"><span class="fl-eyebrow-dot" />Related services</span>
      <h2 class="fl-heading">Related capabilities</h2>
      <p class="fl-subcopy" :class="isLight ? 'text-black opacity-70' : 'text-white opacity-70'">Flutter is one of the cross-platform paths we build with. Explore the other mobility services our teams deliver.</p>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 mt-10 lg:mt-14 px-4 max-w-[1100px] mx-auto">
      <NuxtLink
        v-for="item in related"
        :key="item.title"
        :to="item.to"
        class="fl-link"
      >
        <span class="fl-link-ic">
          <svg
            v-if="item.reactAtom"
            viewBox="0 0 24 24"
            class="size-5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            :class="isLight ? 'text-[#501D71]' : 'text-[#12DBFF]'"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="1.7" fill="currentColor" stroke="none" />
            <ellipse cx="12" cy="12" rx="10" ry="4.2" />
            <ellipse cx="12" cy="12" rx="10" ry="4.2" transform="rotate(60 12 12)" />
            <ellipse cx="12" cy="12" rx="10" ry="4.2" transform="rotate(120 12 12)" />
          </svg>
          <WrIcon v-else :name="item.svgIcon" class="size-5" :class="isLight ? 'text-[#501D71]' : 'text-[#12DBFF]'" />
        </span>
        <span class="fl-link-title">{{ item.title }}</span>
        <span class="fl-link-arrow">
          <svg viewBox="0 0 24 24" class="size-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </span>
      </NuxtLink>
    </div>
  </section>
</template>

<style scoped>
.fl-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.2em;
  color: #887fff; margin-bottom: 14px;
}
.fl-eyebrow-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #12dbff; box-shadow: 0 0 8px rgba(18, 219, 255, 0.8);
  animation: fl-blink 2.4s ease-in-out infinite;
}
@keyframes fl-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
@media (min-width: 1024px) { .fl-eyebrow { font-size: 12px; } }

.fl-heading {
  font-family: "ITC Avant Garde Gothic Demi", "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: clamp(28px, 4vw, 48px); font-weight: 600; line-height: 1.1; text-align: center;
  background: linear-gradient(92deg, #12dbff -3.57%, #ba55fe 80.72%);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: transparent;
}
.fl-subcopy {
  font-family: "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: 16px; line-height: 1.6; letter-spacing: 0.36px; margin-top: 16px;
  text-align: center; max-width: 720px; margin-left: auto; margin-right: auto; padding: 0 16px;
}
@media (min-width: 1024px) { .fl-subcopy { font-size: 18px; } }

/* Compact link pills — a style not used by any other section on this page */
.fl-link {
  position: relative; overflow: hidden;
  display: flex; align-items: center; gap: 12px;
  border-radius: 14px; border: 1px solid rgba(136, 127, 255, 0.15);
  background: rgba(18, 0, 44, 0.4); padding: 14px 18px;
  transition: transform 0.3s ease, border-color 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
}
.fl-link:hover {
  transform: translateY(-2px);
  border-color: rgba(186, 85, 254, 0.4);
  background: rgba(18, 0, 44, 0.6);
  box-shadow: 0 14px 30px -20px rgba(186, 85, 254, 0.5);
}

.fl-link-ic {
  width: 38px; height: 38px; flex-shrink: 0; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid rgba(18, 219, 255, 0.25); background: rgba(18, 219, 255, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.fl-link:hover .fl-link-ic {
  transform: scale(1.06);
  box-shadow: 0 0 16px rgba(18, 219, 255, 0.3);
}

.fl-link-title {
  flex: 1; min-width: 0;
  font-size: 15px; font-weight: 600; color: #fff;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
@media (min-width: 1024px) { .fl-link-title { font-size: 16px; } }

.fl-link-arrow {
  flex-shrink: 0; color: #12dbff; opacity: 0.55;
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fl-link:hover .fl-link-arrow { opacity: 1; transform: translateX(4px); }

/* Light theme */
html:not(.my-app-dark) .fl-eyebrow { color: #613faa; }
html:not(.my-app-dark) .fl-link {
  background: #f6f4fb;
  border-color: rgba(208, 208, 208, 0.6);
}
html:not(.my-app-dark) .fl-link:hover {
  background: #fff;
  border-color: rgba(186, 85, 254, 0.35);
  box-shadow: 0 8px 20px rgba(166, 85, 246, 0.15);
}
html:not(.my-app-dark) .fl-link-ic {
  background: linear-gradient(135deg, rgba(18, 219, 255, 0.15) 0%, rgba(186, 85, 254, 0.2) 100%);
  border-color: transparent;
}
html:not(.my-app-dark) .fl-link-title { color: #613faa; }
html:not(.my-app-dark) .fl-link-arrow { color: #613faa; }
</style>
